/** ADM-08 Catégories et marques. */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, TextField, useToast } from '@myron/design-system';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { del, post, put } from '../../../shared/api/client';
import { SiPermission } from '../../../shared/auth/session';
import { EntetePage, EtatRequete, ErreurFormulaire } from '../../../shared/ui/composants';
import { useCategories, useMarques, type Categorie, type Marque } from './commun';

function Edition({ libelle, onEnregistrer, enCours }: { libelle: string; onEnregistrer: (v: string) => void; enCours: boolean }) {
  const [v, setV] = useState(libelle);
  return (
    <form className="my-ligne" onSubmit={(e) => { e.preventDefault(); if (v.trim()) onEnregistrer(v.trim()); }}>
      <TextField label="Libellé" value={v} onChange={(e) => setV(e.target.value)} />
      <Button type="submit" compact chargement={enCours}>Enregistrer</Button>
    </form>
  );
}

export function PageReferentiels() {
  const qc = useQueryClient();
  const toast = useToast();
  const cats = useCategories();
  const marques = useMarques();
  const [nouvelleCat, setNouvelleCat] = useState('');
  const [nouvelleMarque, setNouvelleMarque] = useState('');
  const [edition, setEdition] = useState<string | null>(null);
  const inval = () => { void qc.invalidateQueries({ queryKey: ['admin', 'categories'] }); void qc.invalidateQueries({ queryKey: ['admin', 'marques'] }); };
  const creerCat = useMutation({ mutationFn: () => post('/admin/categories', { libelle: nouvelleCat.trim() }), onSuccess: () => { setNouvelleCat(''); inval(); toast({ ton: 'succes', titre: 'Catégorie créée' }); } });
  const majCat = useMutation({ mutationFn: (v: { c: Categorie; libelle: string }) => put(`/admin/categories/${v.c.id}`, { libelle: v.libelle }, { version: v.c.version }), onSuccess: () => { setEdition(null); inval(); } });
  const supprCat = useMutation({ mutationFn: (c: Categorie) => del(`/admin/categories/${c.id}`, { version: c.version }), onSuccess: inval });
  const creerMarque = useMutation({ mutationFn: () => post('/admin/marques', { libelle: nouvelleMarque.trim() }), onSuccess: () => { setNouvelleMarque(''); inval(); toast({ ton: 'succes', titre: 'Marque créée' }); } });
  const majMarque = useMutation({ mutationFn: (v: { m: Marque; libelle: string }) => put(`/admin/marques/${v.m.id}`, { libelle: v.libelle }, { version: v.m.version }), onSuccess: () => { setEdition(null); inval(); } });

  return (
    <>
      <EntetePage titre="Catégories et marques" fil={[{ libelle: 'Produits', vers: '/admin/produits' }, { libelle: 'Référentiels' }]} />
      <div className="my-grille" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <Card as="section" aria-labelledby="t-cat">
          <h2 id="t-cat" className="my-titre-4">Catégories</h2>
          <SiPermission permission="produit.gerer">
            <form className="my-ligne" style={{ margin: '12px 0' }} onSubmit={(e) => { e.preventDefault(); if (nouvelleCat.trim()) creerCat.mutate(); }}>
              <TextField label="Nouvelle catégorie" value={nouvelleCat} onChange={(e) => setNouvelleCat(e.target.value)} />
              <Button type="submit" compact icone={Plus} chargement={creerCat.isPending}>Ajouter</Button>
            </form>
          </SiPermission>
          <ErreurFormulaire erreur={creerCat.error ?? majCat.error ?? supprCat.error} />
          <EtatRequete requete={cats} vide={(d) => !d.length} titreVide="Aucune catégorie" enfants={(d) => (
            <ul className="my-pile my-pile--serre" style={{ listStyle: 'none', padding: 0 }}>
              {d.map((c) => (
                <li key={c.id} className="my-ligne my-ligne--espace">
                  {edition === c.id ? <Edition libelle={c.libelle} enCours={majCat.isPending} onEnregistrer={(l) => majCat.mutate({ c, libelle: l })} /> : <span>{c.libelle}</span>}
                  <SiPermission permission="produit.gerer">
                    <span className="my-ligne">
                      <Button compact variante="tertiaire" icone={Pencil} onClick={() => setEdition(edition === c.id ? null : c.id)} aria-label={`Renommer ${c.libelle}`}>Renommer</Button>
                      <Button compact variante="tertiaire" icone={Trash2} onClick={() => supprCat.mutate(c)} aria-label={`Supprimer ${c.libelle}`}>Supprimer</Button>
                    </span>
                  </SiPermission>
                </li>
              ))}
            </ul>
          )} />
        </Card>
        <Card as="section" aria-labelledby="t-mq">
          <h2 id="t-mq" className="my-titre-4">Marques</h2>
          <SiPermission permission="produit.gerer">
            <form className="my-ligne" style={{ margin: '12px 0' }} onSubmit={(e) => { e.preventDefault(); if (nouvelleMarque.trim()) creerMarque.mutate(); }}>
              <TextField label="Nouvelle marque" value={nouvelleMarque} onChange={(e) => setNouvelleMarque(e.target.value)} />
              <Button type="submit" compact icone={Plus} chargement={creerMarque.isPending}>Ajouter</Button>
            </form>
          </SiPermission>
          <ErreurFormulaire erreur={creerMarque.error ?? majMarque.error} />
          <EtatRequete requete={marques} vide={(d) => !d.length} titreVide="Aucune marque" enfants={(d) => (
            <ul className="my-pile my-pile--serre" style={{ listStyle: 'none', padding: 0 }}>
              {d.map((m) => (
                <li key={m.id} className="my-ligne my-ligne--espace">
                  {edition === m.id ? <Edition libelle={m.libelle} enCours={majMarque.isPending} onEnregistrer={(l) => majMarque.mutate({ m, libelle: l })} /> : <span>{m.libelle}</span>}
                  <SiPermission permission="produit.gerer">
                    <Button compact variante="tertiaire" icone={Pencil} onClick={() => setEdition(edition === m.id ? null : m.id)} aria-label={`Renommer ${m.libelle}`}>Renommer</Button>
                  </SiPermission>
                </li>
              ))}
            </ul>
          )} />
        </Card>
      </div>
    </>
  );
}
